import type {
  AgentBinding,
  AgentStatusNames,
  ApiToken,
  CreateApiTokenBody,
  CreateApiTokenResponse,
  ProjectRepository,
  ProjectRepositoryTest,
  ProjectRepositoryWriteTest,
  UpdateProjectRepositoryBody,
} from '@tasks/contracts';

import { apiRequest, apiRequestList, apiRequestVoid } from './client';

/**
 * Токены публичного API проекта (docs/12 §Приложение A.1).
 *
 * Доступ только у администратора проекта: токен открывает данные проекта
 * наружу, поэтому выпуск приравнен к управлению проектом.
 *
 * Вебхуков здесь нет намеренно: подписки настраиваются исключительно через
 * публичный `/api/v1/webhooks` с токеном `webhooks:write`, своего внутреннего
 * API и UI у них не осталось (docs/12 §9).
 */

export function listApiTokens(key: string, signal?: AbortSignal): Promise<ApiToken[]> {
  return apiRequestList<ApiToken>(`/projects/${encodeURIComponent(key)}/api-tokens`, { signal });
}

/** Секрет возвращается один раз: показать его нужно сразу (docs/12 §2.1). */
export async function createApiToken(
  key: string,
  body: CreateApiTokenBody,
): Promise<CreateApiTokenResponse> {
  // Огибающей ресурса здесь нет: ответ несёт пару `token` + `secret`, и
  // разворот по ключу `token` потерял бы секрет.
  return apiRequest<CreateApiTokenResponse>(`/projects/${encodeURIComponent(key)}/api-tokens`, {
    method: 'POST',
    body,
  });
}

export function revokeApiToken(key: string, tokenId: string): Promise<void> {
  return apiRequestVoid(
    `/projects/${encodeURIComponent(key)}/api-tokens/${encodeURIComponent(tokenId)}`,
    { method: 'DELETE' },
  );
}

/** Репозиторий проекта: URL, ветка и read-only токен (docs/17 §5.5). */
export function getProjectRepository(
  key: string,
  signal?: AbortSignal,
): Promise<ProjectRepository> {
  return apiRequest<ProjectRepository>(`/projects/${encodeURIComponent(key)}/repository`, {
    signal,
  });
}

export function updateProjectRepository(
  key: string,
  body: UpdateProjectRepositoryBody,
): Promise<ProjectRepository> {
  return apiRequest<ProjectRepository>(`/projects/${encodeURIComponent(key)}/repository`, {
    method: 'PUT',
    body,
  });
}

export function testProjectRepository(key: string): Promise<ProjectRepositoryTest> {
  return apiRequest<ProjectRepositoryTest>(
    `/projects/${encodeURIComponent(key)}/repository/test`,
    { method: 'POST' },
  );
}

/** Проверка write+PR прав Git-бота разработчика (docs/18 §5.5). */
export function testProjectRepositoryWrite(
  key: string,
): Promise<ProjectRepositoryWriteTest> {
  return apiRequest<ProjectRepositoryWriteTest>(
    `/projects/${encodeURIComponent(key)}/repository/test-write`,
    { method: 'POST' },
  );
}


/** Привязки общих AI-агентов к проекту (docs/18 §7). */
export function listProjectAgents(
  key: string,
  signal?: AbortSignal,
): Promise<{ items: AgentBinding[] }> {
  return apiRequest<{ items: AgentBinding[] }>(
    `/projects/${encodeURIComponent(key)}/agents`,
    { signal },
  );
}

export function addProjectAgent(key: string, agentId: string): Promise<AgentBinding> {
  return apiRequest<AgentBinding>(`/projects/${encodeURIComponent(key)}/agents`, {
    method: 'POST',
    body: { agentId },
  });
}

export function updateProjectAgent(
  key: string,
  agentId: string,
  statuses: AgentStatusNames,
): Promise<AgentBinding> {
  return apiRequest<AgentBinding>(
    `/projects/${encodeURIComponent(key)}/agents/${encodeURIComponent(agentId)}`,
    { method: 'PATCH', body: { statuses } },
  );
}

export function removeProjectAgent(key: string, agentId: string): Promise<void> {
  return apiRequestVoid(
    `/projects/${encodeURIComponent(key)}/agents/${encodeURIComponent(agentId)}`,
    { method: 'DELETE' },
  );
}
